import request from 'superagent';
import api from './core';

/**
 * Static list of document states to match the model's enum
 */
export function documentStates() {
    return [
        {value: 'TO_BE_REVIEWED', label: 'À étudier'},
        {value: 'REJECTED', label: 'Rejetté'},
        {value: 'ACCEPTED', label: 'Accepté'}
    ];
}

/**
 * Fetches one page of operations
 *
 * @param page {Number} Page number
 * @param contract {Number} Contract ID
 */
export function fetchPage({page = 1, contract}) {
    return api.prepare(
        request.get(api.url(`/operations/`))
    ).query({page, contract});
}

/**
 * Fetches all the pages of the API at once
 *
 * @param contract {Number} ID of the contract
 */
export async function listAll({contract}) {
    let next = 1;
    const out = [];

    while (next) {
        const page = (await fetchPage({page: next, contract})).body;

        if (page.next) {
            next += 1;
        } else {
            next = false;
        }

        out.push(...page.results);
    }

    return out;
}

/**
 * Sets a document's expiration date
 *
 * @param expiration {String} ISO date
 * @param document {Number} Document ID
 */
export function setExpiration({expiration, document}) {
    return api.forwardResult(api.prepare(
        request.post(api.url('/operations/set_document_expiration/'))
    ).send({expiration: expiration || null, document}), 'id');
}

/**
 * Sets a document's state
 *
 * @param state {String} One of the allowed states (see `documentStates()`)
 * @param document {Number} Document ID
 * @return {Promise<unknown>}
 */
export function setState({state, document}) {
    return api.forwardResult(api.prepare(
        request.post(api.url('/operations/set_document_state/'))
    ).send({state, document}), 'id');
}

/**
 * Deletes a document
 *
 * @param document {Number} Document ID
 * @return {Promise<unknown>}
 */
export function deleteDocument({document}) {
    return api.forwardResult(api.prepare(
        request.post(api.url('/operations/delete_document/'))
    ).send({document}), 'id');
}

/**
 * Sends a document to the API
 *
 * @param operation Operation ID
 * @param file File ID
 * @param type Document type ID
 * @return {Promise | Promise<unknown>}
 */
export function addDocument({operation, file, type}) {
    return api.forwardResult(api.prepare(
        request.post(api.url(
            `/operations/${encodeURIComponent(operation)}/add_document/`
        ))
    ).send({file, type}), 'id');
}

/**
 * Fetches an individual operation's content
 * @param operation Operation ID
 */
export function getOperation({operation}) {
    return api.forwardResult(api.prepare(
        request.get(api.url(
            `/operations/${encodeURIComponent(operation)}/`
        ))
    ), 'id');
}

export function deleteOperation({operation}) {
    return api.prepare(
        request.post(api.url(
            `/operations/${encodeURIComponent(operation)}/delete_operation/`
        ))
    );
}

/**
 * Starts the contract submission
 * @param operation Operation ID
 */
export function submitContract({operation}) {
    return api.forwardResult(api.prepare(
        request.post(api.url(
            `/operations/${encodeURIComponent(operation)}/submit_contract/`
        ))
    ), 'id');
}

/**
 * Sets the Saxo migration operation as done.
 * @param operation Operation ID
 */
export function setSaxoMigrationAsDone({operation}) {
    return api.prepare(
        request.post(api.url(
            `/operations/${encodeURIComponent(operation)}/saxo_migration_as_done/`
        ))
    );
}

/**
 * Starts the operation submission
 * @param operation Operation ID
 */
export function submitOperation({operation}) {
    return api.forwardResult(api.prepare(
        request.post(api.url(
            `/operations/${encodeURIComponent(operation)}/submit_operation/`
        ))
    ), 'id');
}

/**
 * Retries the operation submission from step 1
 * @param operation Operation ID
 */
export function retryOperation({operation}) {
    return api.forwardResult(api.prepare(
        request.post(api.url(
            `/operations/${encodeURIComponent(operation)}/retry/`
        ))
    ), 'id');
}

/**
 * Set signatures dates of the operation (not the contract)
 *
 * @param operation Operation ID
 * @param alpinia Alpinia signature date
 * @param member Member signature date
 */
export function setSignatures({operation, signatures: [alpinia, member]}) {
    return api.forwardResult(api.prepare(
        request.post(api.url(
            `/operations/${encodeURIComponent(operation)}/set_signatures/`
        ))
    ).send({alpinia, member}), 'id');
}

/**
 * Fetches a ZIP file with all the documents of a specific operation, as a part
 * of the Saxo flow.
 */
export function operationDocumentsToZip({operation}) {
    return api.prepare(
        request.get(api.url(
            `/operations/${encodeURIComponent(operation)}/documents_to_zip/`
        ))
    );
}



// WEBPACK FOOTER //
// ./src/api/operations.js